// src/cplConfig.js
// ─────────────────────────────────────────────────────────────
// Point-in-time cost per letter — its settings, letter sources and shelf
// counts (10/11/26). Table `cost_per_letter_config`
// (supabase/2026-10-11-cost-per-letter-config.sql, NOT run yet — not in
// RUN-ME-all): ONE row per owner per key ("settings" | "letterSources" |
// "shelfCounts"), own-rows RLS, so Max never sees Reece's and never touches
// `settings` (Max reads settings; the calculator prices from rate_sheets).
// One upsert of ONE row per change; before writing, the row is read again
// and merged (letterSources by import id, shelfCounts by component + day,
// settings whole). localStorage "loanflow_cpl_config_<owner>" mirrors it
// {v:1, keys, updatedAt, dirty} and is the only copy while the table is
// missing (42P01 / PGRST205 → latched for the session) or Supabase isn't
// configured; the first session that finds the table uploads the dirty keys
// whose local copy is newer. PGRST002 (a brief 503) is retryable, never the
// latch. Practice mode keeps everything in memory ("for this session only").
// The db is passed in (the contactLog pattern) — this module imports none.
// ─────────────────────────────────────────────────────────────

import { normalizeSettings, DEFAULTS, LOT_COMPS } from './supplyLots';

export const CPL_TABLE = "cost_per_letter_config";
export const CPL_SQL_FILE = "supabase/2026-10-11-cost-per-letter-config.sql";
export const CPL_LS_PREFIX = "loanflow_cpl_config_";
export const CPL_KEYS = ["settings", "letterSources", "shelfCounts"];
const COMPS = LOT_COMPS.concat(["Postage"]);

let tableState = "unknown";
export function cplTableState() { return tableState; }
export function resetCplTable() { tableState = "unknown"; }
function columnMissing(err) { return !!err && (err.code === "42703" || err.code === "PGRST204" || /column .* does not exist|could not find the '[^']*' column/i.test(err.message || "")); }
// The table isn't there (yet): latch for the session. A brief PostgREST 503
// (PGRST002) or a missing column is not that.
export function cplTableMissing(err) {
  if (!err || columnMissing(err) || err.code === "PGRST002") return false;
  return err.code === "42P01" || err.code === "PGRST205" || /could not find the table|relation "?[\w.]*cost_per_letter_config"? does not exist/i.test(err.message || "");
}
function isDay(s) { return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "")); }
function msOf(t) { const n = Date.parse(t || ""); return isNaN(n) ? 0 : n; }
function nowIso() { return new Date().toISOString(); }

export function defaultCplConfig() { return { settings: {}, letterSources: [], shelfCounts: [] }; }
// Settings kept as what DIFFERS from the defaults (a default changed later
// reaches every stored config), each field clamped by normalizeSettings —
// except "Cards in envelopes through": once he sets it (Aug 31 included) it
// is kept, because a missing one falls back to this browser's old pref
// (the "Checked against stamps" control) — compacting Aug 31 away made it
// impossible to set back.
export function compactSettings(raw) {
  const r = raw || {};
  const full = normalizeSettings(r);
  const out = {};
  Object.keys(full).forEach(function (k) {
    if (k === "v") return;
    if (k === "cardsUntil" && (r.cardsUntil === "" || r.cardsUntil === null || full.cardsUntil === r.cardsUntil)) { out[k] = full[k]; return; }
    if (JSON.stringify(full[k]) !== JSON.stringify(DEFAULTS[k])) out[k] = full[k];
  });
  return out;
}
const SOURCE_KEYS = ["id", "importedAt", "asOf", "window", "windowHow", "files", "notExports", "days", "leftOut", "summaryCheck", "totals"];
// A stored letter source: only its aggregate fields (never a name or an address).
function cleanSource(s) {
  if (!s || typeof s !== "object" || !s.id || !s.window || !isDay(s.window.from) || !isDay(s.window.to) || !s.days || typeof s.days !== "object") return null;
  const o = {};
  SOURCE_KEYS.forEach(function (k) { if (s[k] !== undefined) o[k] = s[k]; });
  const days = {};
  Object.keys(s.days).forEach(function (d) {
    if (!isDay(d)) return; const r = s.days[d] || {};
    days[d] = { p: Math.max(0, Math.round(Number(r.p) || 0)), hw: Math.max(0, Math.round(Number(r.hw) || 0)), e: Math.max(0, Math.round(Number(r.e) || 0)), w: Math.max(0, Math.round(Number(r.w) || 0)) };
    const we = Math.min(days[d].w, Math.max(0, Math.round(Number(r.we) || 0)));   // the envelope-only pieces among w (only when any)
    if (we) days[d].we = we;
  });
  o.days = days;
  o.files = (Array.isArray(s.files) ? s.files : []).map(function (f) { return { name: String((f && f.name) || "").slice(0, 200), kind: f && f.kind, rows: Math.max(0, Math.round(Number(f && f.rows) || 0)) }; });
  return o;
}
function cleanCount(c) {
  if (!c || COMPS.indexOf(c.comp) < 0 || !isDay(c.day)) return null;
  const u = Math.round(Number(c.units));
  if (!isFinite(u) || u < 0) return null;
  return { comp: c.comp, day: c.day, units: u };
}
// Anything stored / typed → the config's shape: settings clamped and
// compacted, sources with aggregates only, counts with a component, a day
// and whole units; unknown keys dropped.
export function normalizeCplConfig(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const seen = new Set();
  const sources = (Array.isArray(r.letterSources) ? r.letterSources : []).map(cleanSource).filter(function (s) { if (!s || seen.has(s.id)) return false; seen.add(s.id); return true; });
  const counts = new Map();
  (Array.isArray(r.shelfCounts) ? r.shelfCounts : []).map(cleanCount).filter(Boolean).forEach(function (c) { counts.set(c.comp + "|" + c.day, c); });
  return { settings: compactSettings(r.settings), letterSources: sources, shelfCounts: Array.from(counts.values()).sort(function (a, b) { return a.day < b.day ? -1 : a.day > b.day ? 1 : a.comp < b.comp ? -1 : 1; }) };
}
function cleanKey(key, value) { const o = {}; o[key] = value; return normalizeCplConfig(o)[key]; }
// Merge a key's value with what's stored for it: sources by import id,
// counts by component + day (the incoming one wins; `removed` = ids /
// "comp|day" keys taken out), settings whole.
export function mergeKey(key, stored, incoming, removed) {
  const gone = new Set(removed || []);
  if (key === "letterSources") {
    const m = new Map();
    (stored || []).forEach(function (s) { if (s && !gone.has(s.id)) m.set(s.id, s); });
    (incoming || []).forEach(function (s) { if (s) m.set(s.id, s); });
    return cleanKey(key, Array.from(m.values()));
  }
  if (key === "shelfCounts") {
    const m = new Map();
    (stored || []).forEach(function (c) { if (c && !gone.has(c.comp + "|" + c.day)) m.set(c.comp + "|" + c.day, c); });
    (incoming || []).forEach(function (c) { if (c) m.set(c.comp + "|" + c.day, c); });
    return cleanKey(key, Array.from(m.values()));
  }
  return cleanKey(key, incoming);
}

// ── This device's copy ───────────────────────────────────────
export function cplLsKey(owner) { return CPL_LS_PREFIX + (owner || "reece"); }
function lsRead(owner) {
  try {
    const raw = window.localStorage.getItem(cplLsKey(owner));
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return null;
    return { keys: normalizeCplConfig(o.keys || {}), updatedAt: o.updatedAt && typeof o.updatedAt === "object" ? o.updatedAt : {}, dirty: (Array.isArray(o.dirty) ? o.dirty : []).filter(function (k) { return CPL_KEYS.indexOf(k) >= 0; }) };
  } catch (e) { return null; }
}
function lsWrite(owner, keys, updatedAt, dirty) {
  try { window.localStorage.setItem(cplLsKey(owner), JSON.stringify({ v: 1, keys: keys, updatedAt: updatedAt, dirty: dirty })); return true; } catch (e) { return false; }
}
// This device's copy (or the defaults) — what the screen starts from.
export function readLocalCplConfig(owner) { const l = lsRead(owner); return l ? l.keys : defaultCplConfig(); }

function warnMissing(err) { try { console.warn("LoanFlow: the cost_per_letter_config table isn't there yet — cost-per-letter settings are kept on this device until " + CPL_SQL_FILE + " is run.", err && (err.message || err)); } catch (e) { /* ignore */ } }
async function readRows(db, owner) {
  const r = await db.from(CPL_TABLE).select("id,owner,key,value,updated_at").eq("owner", owner);
  if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
  return Array.isArray(r.data) ? r.data : [];
}
async function upsertKey(db, owner, key, value, at) {
  const row = { id: owner + ":" + key, owner: owner, key: key, value: value, updated_at: at };
  const r = await db.from(CPL_TABLE).upsert(row, { onConflict: "id" }).select("id");
  if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
  if (!Array.isArray(r.data) || !r.data.some(function (x) { return x && x.id === row.id; })) throw new Error("The database didn't confirm the save.");
  return true;
}

// → {config, mode: "server" | "local" | "memory", localReason: "offline" |
// "no_table" | null, error, pushed}. The server's rows win unless this
// device has a newer unsent change for that key (it is uploaded then).
export async function loadCplConfig(db, owner, opts) {
  const o = opts || {};
  owner = owner || "reece";
  const local = lsRead(owner);
  const base = local ? local.keys : defaultCplConfig();
  if (o.practice) return { config: base, mode: "memory", localReason: null, error: null, pushed: 0 };
  if (!db || o.ready === false) return { config: base, mode: "local", localReason: "offline", error: null, pushed: 0 };
  if (tableState === "missing") return { config: base, mode: "local", localReason: "no_table", error: null, pushed: 0 };
  let rows;
  try { rows = await readRows(db, owner); }
  catch (e) {
    if (cplTableMissing(e)) { tableState = "missing"; warnMissing(e); return { config: base, mode: "local", localReason: "no_table", error: null, pushed: 0 }; }
    return { config: base, mode: "unknown", localReason: null, error: (e && e.message) || String(e), pushed: 0 };
  }
  tableState = "present";
  const cfg = defaultCplConfig();
  const at = {};
  rows.forEach(function (r) { if (r && CPL_KEYS.indexOf(r.key) >= 0) { cfg[r.key] = cleanKey(r.key, r.value); at[r.key] = r.updated_at; } });
  let pushed = 0;
  const dirty = local ? local.dirty.slice() : [];
  for (let i = 0; i < dirty.length; i++) {
    const k = dirty[i];
    if (at[k] && msOf(local.updatedAt[k]) <= msOf(at[k])) continue;   // the server's is newer
    const merged = mergeKey(k, cfg[k], local.keys[k]);
    try { await upsertKey(db, owner, k, merged, local.updatedAt[k] || nowIso()); cfg[k] = merged; pushed++; }
    catch (e) { if (cplTableMissing(e)) { tableState = "missing"; break; } }
  }
  lsWrite(owner, cfg, Object.assign({}, local ? local.updatedAt : {}, at), []);
  return { config: cfg, mode: "server", localReason: null, error: null, pushed: pushed };
}
// Settings with a patch laid over what's stored: only the keys this save
// changed (review S2 — a tab loaded before another device's change used to
// put its whole stale copy back: the mailed-through date, "Not a letter
// cost", "On the shelf from"…). A patched key back at its default comes out.
function patchSettings(stored, patch) { return compactSettings(Object.assign({}, stored || {}, patch || {})); }
// Save ONE key (re-read + merged first). → {mode, saved, error, value}
// (value = what is stored now). opts: {practice, ready, removed, patch
// (settings: only these keys over the stored row), current (Practice mode:
// the list on screen — what this change merges into)}.
export async function saveCplKey(db, owner, key, value, opts) {
  const o = opts || {};
  owner = owner || "reece";
  if (CPL_KEYS.indexOf(key) < 0) return { mode: "none", saved: false, error: "Unknown setting.", value: null };
  const patched = key === "settings" && o.patch && typeof o.patch === "object";
  const mergeInto = (stored) => (patched ? patchSettings(stored, o.patch) : mergeKey(key, stored, value, o.removed));
  // Practice: memory only, merged into what's on screen (review S1 — it used to
  // merge into nothing, so a second shelf count replaced the first and Remove
  // emptied the list).
  if (o.practice) return { mode: "memory", saved: false, error: null, value: mergeInto(o.current !== undefined ? cleanKey(key, o.current) : null) };
  const at = nowIso();
  const local = lsRead(owner) || { keys: defaultCplConfig(), updatedAt: {}, dirty: [] };
  const localVal = mergeInto(local.keys[key]);
  local.keys[key] = localVal; local.updatedAt[key] = at;
  if (local.dirty.indexOf(key) < 0) local.dirty.push(key);
  const lsOk = lsWrite(owner, local.keys, local.updatedAt, local.dirty);
  const localErr = lsOk ? null : "This device's copy couldn't be written.";
  if (!db || o.ready === false) return { mode: "local", localReason: "offline", saved: lsOk, error: localErr, value: localVal };
  if (tableState === "missing") return { mode: "local", localReason: "no_table", saved: lsOk, error: localErr, value: localVal };
  try {
    let stored = null;
    const rows = await readRows(db, owner);
    rows.forEach(function (r) { if (r && r.key === key) stored = cleanKey(key, r.value); });
    const merged = mergeInto(stored);
    await upsertKey(db, owner, key, merged, at);
    tableState = "present";
    local.keys[key] = merged; local.dirty = local.dirty.filter(function (k) { return k !== key; });
    lsWrite(owner, local.keys, local.updatedAt, local.dirty);
    return { mode: "server", localReason: null, saved: true, error: null, value: merged };
  } catch (e) {
    if (cplTableMissing(e)) { if (tableState !== "missing") { tableState = "missing"; warnMissing(e); } return { mode: "local", localReason: "no_table", saved: lsOk, error: localErr, value: localVal }; }
    return { mode: "local", localReason: "error", saved: lsOk, error: (e && e.message) || String(e), value: localVal };
  }
}
